/**
 * TARS bar chart — horizontal bars (amCharts ColumnSeries in horizontal layout).
 * Categories on the Y axis (left); values on the X axis (read by bar length).
 * Same table JSON as `TARSColumnChart` via `tarsBarChartHelpers.ts`.
 *
 * Static amCharts imports keep `window.am5` / `window.am5xy` ready before mount.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-ignore amCharts5 bundled IIFE registers globals
import "../../../libs/amcharts5/index";
// @ts-ignore amCharts5 xy bundle
import "../../../libs/amcharts5/xy";
// @ts-ignore Animated theme registers am5themes_Animated
import "../../../libs/amcharts5/themes/Animated";

import type { MutableRefObject } from "react";
import { useLayoutEffect, useMemo, useRef } from "react";
import {
    orderBarChartPoints,
    resolveTarsBarColumnValueSuffix,
    rowsToTARSBarSeries,
    resolveLabelFillHex,
    type TARSBarChartPoint,
    type TARSBarChartTableData,
    type TARSChartConfiguration,
} from "./tarsBarChartHelpers";
import { bindBarColumnFillAdapter } from "./tarsChartFillHelpers";
import { resolveTarsChartFontSizes, type TarsChartFontSizes } from "./tarsChartFontHelpers";
import {
    TARS_COLUMN_BAR_CORNER_RADIUS_PX,
    TARS_HORIZONTAL_BAR_LABEL_TO_BAR_GAP_PX,
} from "./tars-chart-column-style";
import { attachAm5ExportingToRoot, type Am5ChartExportApi } from "./tarsAm5Exporting";
import { prependRootTitleIfAny } from "./tarsAm5RootTitle";
import { bindAm5ExportRefs, clearAm5ExportRefs } from "./tars-chart-export-refs";
import TARSChartCopyImageToolbar from "./TARSChartCopyImageToolbar";

const TARS_BAR_CATEGORY_LABEL_MAX_WIDTH_PX = 220;
const TARS_BAR_CATEGORY_AXIS_MIN_WIDTH_PX = 240;
const TARS_HORIZONTAL_BAR_VALUE_NUMBER_FORMAT = "#.#";
const TARS_HORIZONTAL_BAR_VALUE_LABEL_INSIDE_THRESHOLD_RATIO = 0.9;
const TARS_HORIZONTAL_BAR_VALUE_LABEL_OUTSIDE_DX_PX = 1;
const TARS_HORIZONTAL_BAR_VALUE_LABEL_INSIDE_DX_PX = -8;

function hideAxisTitleIfPresent(axis: any): void {
    try {
        const title = axis.get("title");
        if (title?.set) title.set("visible", false);
    } catch {
        /* optional */
    }
}

/** Horizontal bars: ValueAxis on X, CategoryAxis on Y. */
function buildHorizontalBarChartOnRoot(
    amChartsCore: any,
    amChartsXY: any,
    chartRoot: any,
    barSeriesPoints: TARSBarChartPoint[],
    categoryLabelFill: string,
    valueLabelFill: string,
    valueSuffix: string,
    fonts: TarsChartFontSizes
): void {
    const maxPointValue = barSeriesPoints.reduce(
        (maxValue, point) => Math.max(maxValue, point.value),
        0
    );
    const shouldPlaceValueLabelInsideBar = (value: number): boolean => {
        if (maxPointValue <= 0) return false;
        return value / maxPointValue >= TARS_HORIZONTAL_BAR_VALUE_LABEL_INSIDE_THRESHOLD_RATIO;
    };

    const valueNumberText = `{valueX.formatNumber('${TARS_HORIZONTAL_BAR_VALUE_NUMBER_FORMAT}')}`;
    const formattedValueText =
        valueSuffix === "" ? `[bold]${valueNumberText}[/]` : `[bold]${valueNumberText}[/]${valueSuffix}`;
    const valueBulletText = formattedValueText;
    const valueTooltipText = `{categoryY}: ${formattedValueText}`;
    const xyChart = chartRoot.container.children.push(
        amChartsXY.XYChart.new(chartRoot, {
            panX: false,
            panY: false,
            wheelX: "none",
            wheelY: "none",
            paddingTop: 16,
            paddingLeft: 28,
            paddingRight: TARS_HORIZONTAL_BAR_LABEL_TO_BAR_GAP_PX,
            paddingBottom: 16,
        })
    );

    const yCategoryAxis = xyChart.yAxes.push(
        amChartsXY.CategoryAxis.new(chartRoot, {
            categoryField: "label",
            renderer: amChartsXY.AxisRendererY.new(chartRoot, {
                inversed: true,
                minGridDistance: 10,
            }),
        })
    );

    hideAxisTitleIfPresent(yCategoryAxis);
    const yRenderer = yCategoryAxis.get("renderer");
    yRenderer.set("minWidth", TARS_BAR_CATEGORY_AXIS_MIN_WIDTH_PX);
    yRenderer.labels.template.setAll({
        visible: true,
        fontSize: fonts.axis,
        fontWeight: "700",
        fill: amChartsCore.color(categoryLabelFill),
        textAlign: "right",
        oversizedBehavior: "wrap",
        width: TARS_BAR_CATEGORY_LABEL_MAX_WIDTH_PX,
        maxWidth: TARS_BAR_CATEGORY_LABEL_MAX_WIDTH_PX,
        lineHeight: 1.25,
        paddingRight: 12,
    });
    yRenderer.grid.template.setAll({ visible: false });

    const xValueAxis = xyChart.xAxes.push(
        amChartsXY.ValueAxis.new(chartRoot, {
            min: 0,
            extraMin: 0,
            renderer: amChartsXY.AxisRendererX.new(chartRoot, {}),
        })
    );

    hideAxisTitleIfPresent(xValueAxis);
    xValueAxis.get("renderer").labels.template.setAll({ visible: false });
    xValueAxis.get("renderer").grid.template.setAll({ visible: false });

    const columnSeries = xyChart.series.push(
        amChartsXY.ColumnSeries.new(chartRoot, {
            name: "Series",
            xAxis: xValueAxis,
            yAxis: yCategoryAxis,
            valueXField: "value",
            categoryYField: "label",
        })
    );

    columnSeries.columns.template.setAll({
        height: amChartsCore.percent(65),
        fill: amChartsCore.color("#fff"),
        strokeWidth: 0,
        strokeOpacity: 0,
        cornerRadiusTL: 0,
        cornerRadiusBL: 0,
        cornerRadiusTR: TARS_COLUMN_BAR_CORNER_RADIUS_PX,
        cornerRadiusBR: TARS_COLUMN_BAR_CORNER_RADIUS_PX,
        tooltipText: valueTooltipText,
    });

    bindBarColumnFillAdapter(amChartsCore, columnSeries);

    columnSeries.bullets.push(() => {
        const valueLabel = amChartsCore.Label.new(chartRoot, {
            text: valueBulletText,
            fill: amChartsCore.color(valueLabelFill),
            centerX: amChartsCore.p0,
            centerY: amChartsCore.p50,
            dx: TARS_HORIZONTAL_BAR_VALUE_LABEL_OUTSIDE_DX_PX,
            fontSize: fonts.dataLabel,
            fontWeight: "700",
            populateText: true,
        });
        valueLabel.adapters.add("centerX", (_centerX: unknown, target: any) => {
            const value = Number(target.dataItem?.dataContext?.value ?? 0);
            return shouldPlaceValueLabelInsideBar(value) ? amChartsCore.p100 : amChartsCore.p0;
        });
        valueLabel.adapters.add("dx", (_dx: unknown, target: any) => {
            const value = Number(target.dataItem?.dataContext?.value ?? 0);
            return shouldPlaceValueLabelInsideBar(value)
                ? TARS_HORIZONTAL_BAR_VALUE_LABEL_INSIDE_DX_PX
                : TARS_HORIZONTAL_BAR_VALUE_LABEL_OUTSIDE_DX_PX;
        });

        return amChartsCore.Bullet.new(chartRoot, {
            locationX: 1,
            locationY: 0.5,
            sprite: valueLabel,
        });
    });

    columnSeries.data.setAll(barSeriesPoints);
    yCategoryAxis.data.setAll(barSeriesPoints);
    xyChart.set("cursor", amChartsXY.XYCursor.new(chartRoot, { behavior: "none" }));
    columnSeries.appear(1000);
    xyChart.appear(1000, 100);
}

function mountAm5BarChartInContainer(
    chartContainerElement: HTMLElement,
    barSeriesPoints: TARSBarChartPoint[],
    chartTitle: string | null,
    configuration: TARSChartConfiguration | undefined,
    valueSuffix: string
): { dispose: () => void; exportApi: Am5ChartExportApi } | null {
    if (typeof window === "undefined" || barSeriesPoints.length === 0) return null;

    const amChartsCore = (window as any).am5;
    const amChartsXY = (window as any).am5xy;
    const animatedTheme = (window as any).am5themes_Animated;

    if (!amChartsCore?.Root?.new || !amChartsXY) {
        console.warn("TARSBarChart: amCharts globals not ready");
        return null;
    }

    const chartRoot = amChartsCore.Root.new(chartContainerElement);
    if (animatedTheme) {
        chartRoot.setThemes([animatedTheme.new(chartRoot)]);
    }
    const fonts = resolveTarsChartFontSizes(configuration);
    prependRootTitleIfAny(amChartsCore, chartRoot, chartTitle, fonts.title);

    const categoryLabelFill = resolveLabelFillHex(configuration?.labelColors, "category");
    const valueLabelFill = resolveLabelFillHex(configuration?.labelColors, "value");

    try {
        buildHorizontalBarChartOnRoot(
            amChartsCore,
            amChartsXY,
            chartRoot,
            barSeriesPoints,
            categoryLabelFill,
            valueLabelFill,
            valueSuffix,
            fonts
        );
    } catch (err) {
        console.error("TARSBarChart: failed to build chart", err);
        chartRoot.dispose();
        return null;
    }

    requestAnimationFrame(() => {
        try {
            chartRoot.resize();
        } catch {
            /* optional */
        }
    });

    const exportApi = attachAm5ExportingToRoot(chartRoot);
    return { dispose: () => chartRoot.dispose(), exportApi };
}

export interface TARSBarChartProps {
    data: TARSBarChartTableData;
    id?: string;
    chartCopyRef?: MutableRefObject<(() => Promise<void>) | null>;
    chartExportRef?: MutableRefObject<(() => Promise<Blob>) | null>;
}

export default function TARSBarChart({
    data,
    id = "tars-bar-chart",
    chartCopyRef: chartCopyRefProp,
    chartExportRef,
}: TARSBarChartProps) {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const disposeChartRef = useRef<(() => void) | null>(null);
    const internalChartCopyRef = useRef<(() => Promise<void>) | null>(null);
    const copyRef = chartCopyRefProp ?? internalChartCopyRef;
    const barSeries = useMemo(
        () => orderBarChartPoints(rowsToTARSBarSeries(data), data.preserveRowOrder),
        [data]
    );
    const valueSuffix = useMemo(
        () =>
            resolveTarsBarColumnValueSuffix(
                Array.isArray(data.headers) ? data.headers : [],
                data.configuration,
                barSeries.map((p) => p.value)
            ),
        [data.headers, data.configuration, barSeries]
    );
    const chartTitle = typeof data.title === "string" && data.title.trim() !== "" ? data.title.trim() : null;

    useLayoutEffect(() => {
        const chartContainerElement = chartContainerRef.current;

        disposeChartRef.current?.();
        disposeChartRef.current = null;
        clearAm5ExportRefs(copyRef, chartExportRef);

        if (!chartContainerElement || barSeries.length === 0) return undefined;

        const chartMountHandle = mountAm5BarChartInContainer(
            chartContainerElement,
            barSeries,
            chartTitle,
            data.configuration,
            valueSuffix
        );

        disposeChartRef.current = chartMountHandle ? () => chartMountHandle.dispose() : null;
        bindAm5ExportRefs(chartMountHandle?.exportApi, copyRef, chartExportRef);

        return () => {
            disposeChartRef.current?.();
            disposeChartRef.current = null;
            clearAm5ExportRefs(copyRef, chartExportRef);
        };
    }, [barSeries, chartTitle, data.configuration, valueSuffix, copyRef]);

    if (barSeries.length === 0) {
        return (
            <div className="flex flex-1 items-center justify-center px-8 py-16 text-[13px] font-medium text-slate-400">
                No numeric rows to display. Paste JSON with headers and rows.
            </div>
        );
    }

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col">
            {!chartCopyRefProp ? (
                <div className="mb-2 flex shrink-0 justify-end">
                    <TARSChartCopyImageToolbar copyRef={internalChartCopyRef} />
                </div>
            ) : null}
            <div
                ref={chartContainerRef}
                id={id}
                className="min-h-0 min-h-[280px] w-full flex-1 basis-0"
                style={{ minHeight: "min(480px, 50vh)" }}
            />
        </div>
    );
}
